import { useContext } from 'react';
import { MutationResultPair, useMutation } from 'react-query';
import API from '../../api';
import GlobalContext from '../../contexts/GlobalContext';
import Endpoints from '../../endpoints';
import { IKafkaEvent, KafkaEventServiceHook, KafkaOptions } from '../../types/kafkaEvent';
import { KAFKA_EVENTS } from '../../config/kafkaConfig';
import { generateUUID } from '../../utils/utils';
import {
  generateLayout, generateSizeString, generateStyleList, generateStyleString
} from '../../utils/kafkaUtils';

export const usePostEvent = (): MutationResultPair<void, unknown, unknown, unknown> => {
  const endPoints = Endpoints();
  const kafkaUrl = endPoints.kafkaUrl();
  const postEvent = async (payload: any): Promise<void> => {
    try {
      await API.post(kafkaUrl, payload);
    } catch (error) {
      // Handle Error Case
    }
  };

  return useMutation((payload): Promise<void> => postEvent(payload), {});
};

export const useKafkaEventService = (): KafkaEventServiceHook => {
  const { globalContext } = useContext(GlobalContext);
  const isMobile = globalContext.gb.utils.checkDevice('mobile');
  const ns = globalContext.liquid.namespaceID;
  const [postEvent] = usePostEvent();

  function generateKafkaPayload(options: KafkaOptions): IKafkaEvent {
    const eventType = options.eventType;
    const uuid = globalContext.customer.uuid;
    let payload: IKafkaEvent = {
      eventId: generateUUID(),
      eventType: eventType,
      eventTimestamp: globalContext.gb.dateUtils.getUTCDateString(),
      tenantNamespace: ns,
      customerUuid: uuid,
      cluster: globalContext.gb.utils.getClusterValue(),
      collectionId: options.collectionId.toString(),
      collectionPageNumber: options.collectionPageNumber ? options.collectionPageNumber : 1,
      sourceEventId: options.sourceEventId
    };

    switch (eventType) {
      case KAFKA_EVENTS.COLLECTION_VIEW_EVENT:
        payload = {
          ...payload,
          styleList: generateStyleList(options.matchedProducts),
          viewedStyleList: generateStyleList(options.viewedStyleList),
          startPosition: options.matchedProducts[0].position,
          layout: generateLayout(
            options.pageSize,
            options.matchedProducts.length,
            isMobile
          ),
          appliedBrandFilters: options.appliedBrandFilters,
          appliedColorFilters: options.appliedColorFilters,
          appliedSizeFilters: options.appliedSizeFilters,
          pageSize: options.pageSize
        };
        break;
      case KAFKA_EVENTS.COLLECTION_SCROLL_EVENT:
        payload = {
          ...payload,
          viewedStyleList: generateStyleList(options.viewedStyleList)
        };
        break;
      case KAFKA_EVENTS.PRODUCT_CLICK_EVENT:
      case KAFKA_EVENTS.PRODUCT_QUICK_CLOSET_DETAILS_CLICK:
      case KAFKA_EVENTS.PRODUCT_QUICK_CLOSET_CLICK:
        payload = {
          ...payload,
          style: options.style,
          position: options.position
        };
        break;
      case KAFKA_EVENTS.PRODUCT_QUICK_CLOSET_EVENT:
        payload = {
          ...payload,
          sku: options.sku,
          chosenSize: generateSizeString(options.sku),
          style: generateStyleString(options.sku)
        };
        break;
      case KAFKA_EVENTS.PRODUCT_CLOSET_EVENT:
        payload = {
          ...payload,
          sku: options.sku,
          chosenSize: generateSizeString(options.sku),
          style: generateStyleString(options.sku)
        };
        break;
      default:
        break;
    }

    return payload;
  }

  const postSizeAdvisorEvent = async (eventObj): Promise<void> => {
    postEvent({
      eventId: generateUUID(),
      eventTs: globalContext.gb.dateUtils.getUTCDateString(),
      cluster: globalContext.gb.utils.getClusterValue(),
      eventFamily: 'rec',
      nameSpaceId: parseInt(ns, 10),
      eventType: 'rec.size.advisor.info',
      versionInfo: {
        pubSchemaVersion: '1.0.0'
      },
      developerFields: {},
      txInfo: {
        sourceEventId: globalContext.emitter.sourceEventId
      },
      eventInfo: {
        ...eventObj
      }
    });
  };
  const emitACGKafkaEvent = async (data: { options: KafkaOptions }): Promise<string> => {
    const payload = generateKafkaPayload(data.options);

    await postEvent(payload);

    return payload.eventId;
  };
  const emitKafkaEvent = async (eventType: string, eventObject: any): Promise<void> => {
    let eventFamily: string;
    let payload: any;

    if (
      eventObject &&
      eventType &&
      typeof eventType === 'string'
    ) {
      eventFamily = eventType.split('.')[0];
    } else {
      return null;
    }

    payload = {
      eventId: generateUUID(),
      cluster: 'web.desktop',
      developersFields: {},
      nameSpaceId: parseInt(ns, 10),
      versionInfo: {
        pubSchemaVersion: '1.0.0'
      },
      eventFamily: eventFamily,
      eventType: eventType,
      eventTs: globalContext.gb.dateUtils.getUTCDateString(),
      receivedTs: globalContext.gb.dateUtils.getUTCDateString(),
      txInfo: {
        sourceEventId: globalContext.emitter.sourceEventId,
        baseEventId: '',
        baseEventTs: ''
      },
      eventInfo: eventObject
    };

    await postEvent(payload);

    return payload.eventId;
  };

  return {
    emitACGKafkaEvent: emitACGKafkaEvent,
    postSizeAdvisorEvent: postSizeAdvisorEvent,
    emitKafkaEvent: emitKafkaEvent,
    postEvent: postEvent
  };
};

export function getNewUrlForProductClick(eventId: string, collectionId: number,
  pageIndex: number, initialURL: string): string {
  const queryParams = {
    eventId: eventId.toString(),
    collectionId: collectionId.toString(),
    pageIndex: pageIndex.toString()
  };
  const queryString = new URLSearchParams(queryParams).toString();
  const separator = initialURL.includes('?') ? '&' : '?';

  return `${initialURL}${separator}${queryString}`;
}
